/* Motion — the parts CSS cannot do on its own.
 *
 * Replaces reveal.js. The engine is CSS scroll-driven animation (base.css §10);
 * this file exists for exactly three jobs:
 *
 *   1. Split display headlines into lines so each can slide out from behind a
 *      mask. Line boxes only exist after layout, so this cannot be done in CSS
 *      or in Liquid.
 *   2. Reveal content on browsers without `animation-timeline` — Firefox today.
 *   3. Choreograph the mobile menu, which is a state change rather than a
 *      scroll position.
 *
 * Everything here is skipped entirely under prefers-reduced-motion. Content is
 * never hidden unless this script is running AND able to reveal it again.
 */

(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  var supportsTimeline = CSS.supports('animation-timeline: view()');

  /* ---------------------------------------------------------------- lines
   * Wrap each rendered line of a headline so the mask reveal has something to
   * move. Rebuilt on resize, because a different width means different lines.
   */
  function splitLines(el) {
    if (!el.dataset.ocOriginal) el.dataset.ocOriginal = el.innerHTML;

    var html = el.dataset.ocOriginal;
    // Work from plain text; these headlines are escaped copy plus <br>.
    var text = html.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '');
    var words = text.split(/(\s+|\n)/).filter(function (w) { return w.length; });

    el.textContent = '';
    var probe = document.createElement('span');
    words.forEach(function (w) {
      if (w === '\n') { probe.appendChild(document.createElement('br')); return; }
      var s = document.createElement('span');
      s.textContent = w;
      probe.appendChild(s);
    });
    el.appendChild(probe);

    // Group the word spans by their vertical offset — that is the line box.
    var lines = [];
    var current = null;
    var lastTop = null;
    Array.prototype.forEach.call(probe.childNodes, function (node) {
      if (node.nodeName === 'BR') { lastTop = null; current = null; return; }
      var top = node.offsetTop;
      if (current === null || top !== lastTop) {
        current = [];
        lines.push(current);
        lastTop = top;
      }
      current.push(node.textContent);
    });

    el.textContent = '';
    lines.forEach(function (words) {
      var outer = document.createElement('span');
      outer.className = 'oc-line';
      var inner = document.createElement('span');
      inner.className = 'oc-line__inner';
      inner.textContent = words.join('').replace(/\s+$/, '');
      outer.appendChild(inner);
      el.appendChild(outer);
    });
  }

  function setupLines() {
    if (reduced.matches) return;
    var targets = document.querySelectorAll('[data-split-lines]');
    if (!targets.length) return;
    Array.prototype.forEach.call(targets, splitLines);
  }

  /* --------------------------------------------------------------- reveal
   * Only for browsers without scroll timelines. The class is what activates
   * the hidden states in CSS, so if this never runs, nothing is ever hidden.
   */
  function setupFallbackReveal() {
    if (reduced.matches || supportsTimeline) return;
    if (!('IntersectionObserver' in window)) return;

    document.documentElement.classList.add('oc-js-reveal');

    /* Phones trigger EARLIER, not later. The desktop margin of -12% shrinks
     * the root box so a block has to be properly on screen before it starts;
     * on a phone that means a thumb-flick can land a block in the middle of
     * the viewport while it is still arriving. A positive bottom margin
     * extends the root box BELOW the fold instead, so the fade has already
     * run by the time the block is readable. 18% of an 812px screen is about
     * 146px of lead-in. The CSS side is matched in base.css §10. */
    var phone = window.matchMedia('(max-width: 767px)').matches;
    var rootMargin = phone ? '0px 0px 18% 0px' : '0px 0px -12% 0px';
    var threshold = phone ? 0 : 0.05;

    var targets = document.querySelectorAll('[data-reveal], [data-reveal-run]');

    /* SAFETY NET. If a block has been inside the viewport for 1.5s and is
     * still not marked visible — a stalled main thread, a dropped callback, a
     * fast scroll that outran the observer — stop waiting and show it. Half-
     * faded text is worse than no animation, so the net always wins. */
    function show(t) {
      t.classList.add('is-visible');
      if (t._ocTimer) { window.clearTimeout(t._ocTimer); t._ocTimer = null; }
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) {
          return;
        }
        show(entry.target);
        io.unobserve(entry.target);
      });
    }, { rootMargin: rootMargin, threshold: threshold });

    /* A second, cheap observer whose only job is to start the 1.5s clock the
     * moment a block touches the viewport. Separate from the one above so the
     * net does not depend on the reveal observer having fired correctly. */
    var guard = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var t = entry.target;
        if (!entry.isIntersecting || t.classList.contains('is-visible')) return;
        if (t._ocTimer) return;
        t._ocTimer = window.setTimeout(function () { show(t); }, 1500);
      });
    }, { rootMargin: '0px', threshold: 0 });

    Array.prototype.forEach.call(targets, function (t) {
      // Anything already on screen appears without animating, so the first
      // paint is never a flash of empty page.
      if (t.getBoundingClientRect().top < window.innerHeight) {
        t.classList.add('is-visible');
      } else {
        io.observe(t);
        guard.observe(t);
      }
    });

    // Belt and braces: if anything is still hidden after 3s, show it.
    window.setTimeout(function () {
      Array.prototype.forEach.call(targets, show);
    }, 3000);
  }

  /* ------------------------------------------------------------------ init */
  function init() {
    setupLines();
    setupFallbackReveal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Lines depend on width. Rebuild them when it changes, debounced.
  var resizeTimer;
  window.addEventListener('resize', function () {
    if (reduced.matches) return;
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(setupLines, 200);
  });
})();
